import { Formik } from 'formik';
import omit from 'lodash/omit';

import { Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  createInsurance, CreateInsuranceInput, createNonPartnerInsurance,
  createNonPartnerInsuranceCompany, deleteInsurance, deleteNonPartnerInsurance, Insurance, NonPartnerInsurance, NonPartnerInsuranceInput,
  updateNonPartnerInsurance, UserProfile
} from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useNonPartnerInsuranceCompanyQuery, useUserWithProfileQuery } from '@uplift/queries';
import { AddressType } from '@uplift/types';
import { useWithAddressVerify } from '@uplift/ui-v2';

import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../../components';
import {
  formBirthDateFormatToBackendFormat, OTHER_NON_PARTNER_INSURANCE_ID, SELF_PAY_ID
} from '../../../utils';
import { InsuranceInfoForm } from './form';
import { InsuranceInfoProps } from './insurance-info';
import { InsuranceInfoFormValues, insuranceInfoValidation } from './validation';

type InsuranceInfoContentProps = InsuranceInfoProps & {
  initialValues: InsuranceInfoFormValues;
  lastNonPartnerInsurance?: NonPartnerInsurance;
  partnerInsurance?: Insurance;
};

const profileToAddressValues = (profile: UserProfile): AddressType => ({
  address1: profile.address.line1 ?? '',
  address2: profile.address.line2 ?? '',
  city: profile.address.city ?? '',
  state: profile.address.stateId ?? '',
  zip: profile.address.zip ?? '',
});

export const InsuranceInfoContent: React.VFC<InsuranceInfoContentProps> = (props) => {
  const isOtherNonPartnerInsuranceSelected = props.insuranceId === OTHER_NON_PARTNER_INSURANCE_ID;
  const { data: nonPartnerInsuranceCompany } = useNonPartnerInsuranceCompanyQuery(props.insuranceId, { enabled: !!props.insuranceId && !props.isPartnerInsurance && !isOtherNonPartnerInsuranceSelected });
  const queryClient = useQueryClient();
  const withAddressVerify = useWithAddressVerify();
  const currentUser = useCurrentuser();
  const { data: userWithProfile } = useUserWithProfileQuery(currentUser.id);
  const l10n = useL10n();

  const onSubmit = withAddressVerify(async (values: InsuranceInfoFormValues) => {
    const isPrimary = values.isPrimarySubscriber;

    const address = isPrimary
      ? profileToAddressValues(userWithProfile.profile)
      : values.address;

    const primarySubscriber = isPrimary
      ? {
        firstName: userWithProfile.user.firstName,
        lastName: userWithProfile.user.lastName,
        dob: userWithProfile.profile.dob,
      }
      : {
        firstName: values.primarySubscriberFirstName,
        lastName: values.primarySubscriberLastName,
        dob: formBirthDateFormatToBackendFormat(values.primarySubscriberDateOfBirth)
      };

    if (props.isPartnerInsurance) {
      if (props.lastNonPartnerInsurance) {
        await deleteNonPartnerInsurance(props.lastNonPartnerInsurance.id);
      }

      const partnerInsuranceData: CreateInsuranceInput = {
        isPrimary: isPrimary,
        companyId: values.insuranceProvider,
        groupNumber: values.groupNumber,
        subscriberId: values.memberId,
        address: {
          line1: address.address1,
          line2: address.address2,
          city: address.city,
          stateId: address.state,
          zip: address.zip,
        },
        cardFrontAssetId: values.insuranceCardFront,
        cardBackAssetId: values.insuranceCardBack,
        isSecondaryInsurance: false,
        ...primarySubscriber
      };

      await createInsurance(partnerInsuranceData);
      await queryClient.invalidateQueries([ 'user', currentUser.id, 'insurances' ]);

      props.onNext({ insuranceId: partnerInsuranceData.companyId });
      return;
    }

    if (props.partnerInsurance) {
      await deleteInsurance(props.partnerInsurance.id);
    }

    const nonPartnerInsuranceData: NonPartnerInsuranceInput = {
      studentId: currentUser.id,
      companyId: values.insuranceProvider,
      groupNumber: values.groupNumber,
      subscriberId: values.memberId,
      holderType: isPrimary ? 'primary' : 'secondary',
      cardPhotos: {
        frontAssetId: values.insuranceCardFront,
        backAssetId: values.insuranceCardBack,
      },
      holderInfo: {
        ...primarySubscriber,
        address: {
          line_1: address.address1,
          line_2: address.address2,
          city: address.city,
          stateId: address.state,
          zip: address.zip,
        },
      },
    };

    // Determine the insurance case type
    const getInsuranceCase = () => {
      if (props.lastNonPartnerInsurance
          && nonPartnerInsuranceCompany?.creatorType === 'student'
          && nonPartnerInsuranceCompany.name !== values.insuranceProviderFreeText) {
        return 'CREATE_NEW_COMPANY_AND_UPDATE_INSURANCE';
      }

      if (props.lastNonPartnerInsurance && !isOtherNonPartnerInsuranceSelected) {
        return 'UPDATE_INSURANCE';
      }

      if (!props.lastNonPartnerInsurance && nonPartnerInsuranceCompany?.creatorType === 'admin') {
        return 'CREATE_INSURANCE';
      }

      if (!props.lastNonPartnerInsurance && isOtherNonPartnerInsuranceSelected) {
        return 'CREATE_COMPANY_AND_INSURANCE';
      }
      console.log('No case found');
    };

    // Handle non-partner insurance cases
    switch (getInsuranceCase()) {
      case 'CREATE_NEW_COMPANY_AND_UPDATE_INSURANCE': {
        // Case 1: Update existing insurance with new student-provided company
        const company = await createNonPartnerInsuranceCompany({
          name: values.insuranceProviderFreeText,
          stateIds: [ props.stateId ],
        });

        nonPartnerInsuranceData.companyId = company.data.id;
        await updateNonPartnerInsurance(props.lastNonPartnerInsurance.id, omit(nonPartnerInsuranceData, 'studentId'));
        props.onNext({ insuranceId: nonPartnerInsuranceData.companyId });
        break;
      }
      case 'UPDATE_INSURANCE': {
        // Case 2: Update existing non-partner insurance
        await updateNonPartnerInsurance(props.lastNonPartnerInsurance.id, omit(nonPartnerInsuranceData, 'studentId'));
        props.onNext({ insuranceId: nonPartnerInsuranceData.companyId });
        break;
      }
      case 'CREATE_INSURANCE': {
        // Case 3: Create new insurance with admin-created company
        await createNonPartnerInsurance(nonPartnerInsuranceData);
        props.onNext({ insuranceId: nonPartnerInsuranceData.companyId });
        break;
      }
      case 'CREATE_COMPANY_AND_INSURANCE': {
        // Case 4: Create new insurance with new student-provided company
        const company = await createNonPartnerInsuranceCompany({
          name: values.insuranceProviderFreeText,
          stateIds: [ props.stateId ],
        });

        nonPartnerInsuranceData.companyId = company.data.id;
        await createNonPartnerInsurance(nonPartnerInsuranceData);
        props.onNext({ insuranceId: nonPartnerInsuranceData.companyId });
        break;
      }
    }

    await queryClient.invalidateQueries([ 'user', currentUser.id, 'insurances' ]);
  }, {
    shouldCheck: (_, values) => !values.isPrimarySubscriber,
    initialAddress: props.initialValues.address,
    getAddressFromFormikValues: (values) => values.address,
  });

  const validateInsuranceFreeText = isOtherNonPartnerInsuranceSelected || nonPartnerInsuranceCompany?.creatorType === 'student';

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={props.initialValues}
      validationSchema={() => insuranceInfoValidation(l10n, validateInsuranceFreeText)}
      enableReinitialize
    >
      {formik => {
        return (
          <StepContainer>
            {props.banner}
            <StepContent>
              <StepHeading>Insurance</StepHeading>
              {!props?.isBookingSponsored && <StepDescription>Upload insurance information to ensure you are billed for the correct amount.</StepDescription>}
              <StepForm onSubmit={formik.handleSubmit}>
                <InsuranceInfoForm
                  isInviteOrDirectFlow={props.isInviteOrDirectFlow}
                  providerId={props.providerId}
                  insuranceId={props.insuranceId}
                  isBookingSponsored={props.isBookingSponsored}
                  isPartnerInsurance={props.isPartnerInsurance}
                />
              </StepForm>
            </StepContent>
            <StepNavigator onBack={props.onBack}>
              <StepNextButton disabled={!formik.isValid || formik.isSubmitting} />
              {props.isInviteOrDirectFlow && <Button onClick={() => props.onNext({ insuranceId: SELF_PAY_ID })}>Skip Insurance</Button>}
            </StepNavigator>
          </StepContainer>
        );
      }}
    </Formik>
  );
};
